import useDerivedAutoHazardGrade from "./useDerivedAutoHazardGrade";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import UsStatesSelector from "components/common/UsStatesSelector";
import { Fragment, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as programActions from "store/actions/input/program/programActions";
import * as programSelectors from "store/selectors/input/program/programSelectors";

// Fallback dropdown options used when the static-data config for
// auto_exposure_rating is missing. Sourced from FSE's Excel rater spreadsheet
// (Auto Exposure Rating tab) on 2026-05-14. Admins can override per-company
// via the auto_exposure_rating_config static-data key (see DEFAULT_CONFIG
// shape below).
const DEFAULT_CONFIG = {
  vehicleUsageOptions: [
    "Local/Service",
    "Local/Commercial",
    "Local/Retail",
    "Intermediate/Service",
    "Intermediate/Commercial",
    "Intermediate/Retail",
    "Long-Haul",
  ],
  settingOptions: ["Rural", "Average", "Urban"],
  autoHazardGradeOptions: ["Low", "Medium", "High"],
};

// Rating-variable definitions from the FSE rater (Auto Exposure Rating tab,
// A26:K32). Shown as a tooltip on Vehicle Usage, whose options combine a
// distance class (Local / Intermediate / Long Distance) with a use type
// (Service / Retail / Commercial).
const VEHICLE_USAGE_DEFINITIONS = [
  [
    "Local (Up To 50 Miles)",
    "The auto is not regularly operated beyond a radius of 50 miles from the street address where such auto is principally garaged.",
  ],
  [
    "Intermediate (51 To 200 Miles)",
    "The auto is operated beyond a radius of 50 miles, but not regularly beyond a radius of 200 miles, from the street address where such auto is principally garaged.",
  ],
  [
    "Long Distance (Over 200 Miles)",
    "The auto is operated regularly beyond a 200-mile radius from the street address where such auto is principally garaged.",
  ],
  [
    "Service Use",
    "Autos used for transporting the insured's personnel, tools, equipment and incidental supplies to or from a job location. This classification is confined to autos principally parked at job locations for the majority of the working day or used to transport supervisory personnel between job locations.",
  ],
  [
    "Retail Use",
    "Autos used to pick up property from, or deliver property to, individual households.",
  ],
  [
    "Commercial Use",
    "Autos used for transporting property other than those autos defined as service or retail.",
  ],
];

// Vehicle types in the order they appear in the FSE rater. Each row carries
// the field key for the manual count (yellow) and the Phase 2 1x1 Minimum
// Rate (blue, read-only) for display. Phase 2 will use these values for
// pricing; Phase 1 displays them so the layout is final.
const VEHICLE_ROWS = [
  { key: "vehiclePpt", label: "PPT", minRate1x1: 200 },
  { key: "vehicleLightTruck", label: "Light Truck", minRate1x1: 300 },
  { key: "vehicleMediumTruck", label: "Medium Truck", minRate1x1: 400 },
  { key: "vehicleHeavyTruck", label: "Heavy Truck", minRate1x1: 600 },
  {
    key: "vehicleExtraHeavyTruck",
    label: "Extra Heavy Truck",
    minRate1x1: 800,
  },
  {
    key: "vehicleHeavyTruckTractor",
    label: "Heavy Truck Tractor",
    minRate1x1: 800,
  },
  {
    key: "vehicleExtraHeavyTruckTractor",
    label: "Extra Heavy Truck Tractor",
    minRate1x1: 1000,
  },
  {
    key: "vehiclePassengerTransport",
    label: "Passenger Transport",
    minRate1x1: 1000,
  },
  { key: "vehicleOther", label: "Other", minRate1x1: 1000 },
];

// PPT Equivalent in the Hired/Non-Owned block is derived: 5 employees = 1 PPT
// equivalent, $15,000 annual cost of hire = 1 PPT equivalent. Confirmed by
// FSE (Ben Alcorn, 2026-05-14).
const computePptEquivalent = (employees, annualCostOfHire) => {
  const e = Number(employees);
  const c = Number(annualCostOfHire);
  const fromEmployees = Number.isFinite(e) ? e / 5 : 0;
  const fromCost = Number.isFinite(c) ? c / 60 / 260 : 0;
  return fromEmployees + fromCost;
};

const isBlank = (v) => v == null || v === "";

const formatPptEquivalent = (employees, annualCostOfHire) => {
  if (isBlank(employees) && isBlank(annualCostOfHire)) {
    return "";
  }
  return computePptEquivalent(employees, annualCostOfHire).toFixed(2);
};

const Section = ({ title, children }) => (
  <Paper variant={"outlined"} sx={{ p: 2 }}>
    <Stack spacing={2}>
      <Typography variant={"subtitle1"} fontWeight={600}>
        {title}
      </Typography>
      {children}
    </Stack>
  </Paper>
);

const NumberField = ({ label, value, onChange, min, sx }) => (
  <Disabled ifReadOnly>
    <PrettyNumberTextField
      label={label}
      InputLabelProps={{ shrink: true }}
      InputProps={{ spellCheck: "false" }}
      inputProps={{ style: { textAlign: "right" }, min }}
      size={"small"}
      variant={"filled"}
      fullWidth
      sx={sx}
      value={value ?? ""}
      onChangeNumber={onChange}
    />
  </Disabled>
);

const ReadOnlyField = ({ label, value }) => (
  <TextField
    label={label}
    InputLabelProps={{ shrink: true }}
    InputProps={{ readOnly: true }}
    inputProps={{ style: { textAlign: "right" } }}
    size={"small"}
    variant={"outlined"}
    fullWidth
    value={value ?? ""}
  />
);

const DefinitionsTooltip = ({ definitions }) => (
  <Tooltip
    arrow
    placement={"bottom-end"}
    title={
      <Stack spacing={0.75} sx={{ py: 0.5 }}>
        {definitions.map(([term, description]) => (
          <Typography key={term} variant={"caption"} component={"span"}>
            <strong>{term}</strong>
            {`: ${description}`}
          </Typography>
        ))}
      </Stack>
    }
  >
    <InfoOutlinedIcon
      fontSize={"small"}
      sx={{ color: "text.secondary", cursor: "help" }}
    />
  </Tooltip>
);

const DropdownField = ({ label, value, onChange, options, definitions }) => (
  <Disabled ifReadOnly>
    <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
      <FormControl fullWidth size={"small"} variant={"filled"}>
        <InputLabel shrink>{label}</InputLabel>
        <Select
          value={value ?? ""}
          label={label}
          displayEmpty
          onChange={(e) => onChange(e.target.value || null)}
        >
          <MenuItem value={""}>
            <em>{"(none)"}</em>
          </MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {definitions && <DefinitionsTooltip definitions={definitions} />}
    </Stack>
  </Disabled>
);

const AutoExposureRating = () => {
  const dispatch = useDispatch();
  const data = useSelector(programSelectors.getAutoExposureRating);
  // Engine-derived auto hazard grade (owned fleet + state + usage + setting),
  // computed live by the engine — never recomputed in the frontend.
  const derivedHazardGrade = useDerivedAutoHazardGrade(data);
  const { data: config } = staticDataService.useConfigQuery(
    "auto_exposure_rating_config"
  );
  const vehicleUsageOptions =
    config?.vehicleUsageOptions ?? DEFAULT_CONFIG.vehicleUsageOptions;
  const settingOptions =
    config?.settingOptions ?? DEFAULT_CONFIG.settingOptions;

  const update = useCallback(
    (field) => (value) =>
      dispatch(programActions.updateAutoExposureRating(field, value)),
    [dispatch]
  );

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Section title={"Vehicles"}>
        <Grid container spacing={1} alignItems={"center"}>
          <Grid item xs={6} />
          <Grid item xs={6}>
            <Typography variant={"caption"} color={"text.secondary"}>
              {"Number of Vehicles"}
            </Typography>
          </Grid>
          {VEHICLE_ROWS.map((row) => (
            <Fragment key={row.key}>
              <Grid item xs={6}>
                <Typography variant={"body2"}>{row.label}</Typography>
              </Grid>
              <Grid item xs={6}>
                <NumberField
                  label={`${row.label} count`}
                  value={data[row.key]}
                  onChange={update(row.key)}
                  min={0}
                />
              </Grid>
            </Fragment>
          ))}
        </Grid>
      </Section>

      <Section title={"Rating Drivers"}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <UsStatesSelector
              label={"Predominant State"}
              stateId={data.predominantState}
              onChange={update("predominantState")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DropdownField
              label={"Vehicle Usage"}
              value={data.vehicleUsage}
              onChange={update("vehicleUsage")}
              options={vehicleUsageOptions}
              definitions={VEHICLE_USAGE_DEFINITIONS}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DropdownField
              label={"Setting"}
              value={data.setting}
              onChange={update("setting")}
              options={settingOptions}
            />
          </Grid>
        </Grid>
      </Section>

      <Section title={"Hired and Non-Owned Auto"}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <NumberField
              label={"Number of Employees"}
              value={data.hnoaEmployees}
              onChange={update("hnoaEmployees")}
              min={0}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <NumberField
              label={"Annual Cost of Hire ($)"}
              value={data.hnoaAnnualCostOfHire}
              onChange={update("hnoaAnnualCostOfHire")}
              min={0}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <ReadOnlyField
              label={"PPT Equivalent (derived)"}
              value={formatPptEquivalent(
                data.hnoaEmployees,
                data.hnoaAnnualCostOfHire
              )}
            />
          </Grid>
        </Grid>
      </Section>

      <Section title={"Auto Hazard Grade"}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ReadOnlyField
              label={"Auto Hazard Grade (derived)"}
              value={derivedHazardGrade ?? "—"}
            />
          </Grid>
        </Grid>
      </Section>

      <Section title={"Premium"}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <NumberField
              label={"Primary Auto Market Charged Premium ($)"}
              value={data.primaryAutoMarketChargedPremium}
              onChange={update("primaryAutoMarketChargedPremium")}
              min={0}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <NumberField
              label={"Primary Auto Market Limit ($)"}
              value={data.primaryAutoMarketLimit}
              onChange={update("primaryAutoMarketLimit")}
              min={0}
            />
          </Grid>
        </Grid>
      </Section>
    </Stack>
  );
};

export default AutoExposureRating;
export {
  computePptEquivalent,
  formatPptEquivalent,
  VEHICLE_ROWS,
  DEFAULT_CONFIG,
  VEHICLE_USAGE_DEFINITIONS,
  DefinitionsTooltip,
};
